import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Eye } from 'lucide-react';

/**
 * Preview dialog showing how custom product options will appear to customers.
 * Uses native HTML elements (no Radix Select/RadioGroup) to avoid
 * compose-refs infinite loop on mobile viewports.
 */
const OptionPreviewDialog = ({ open, onOpenChange, options, basePrice }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Eye className="h-5 w-5" />
          Customer View Preview
        </DialogTitle>
        <DialogDescription>
          This is how your product options will appear to customers on the storefront
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-5 py-4">
        {options.map((option) => (
          <div key={option.id} className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-1">
              {option.name || 'Unnamed Option'}
              {option.required && <span className="text-red-500">*</span>}
            </Label>
            {option.helpText && <p className="text-xs text-gray-500">{option.helpText}</p>}
            <PreviewInput option={option} />
          </div>
        ))}
        {options.some(opt => opt.choices?.some(c => c.price > 0)) && (
          <PriceSummary basePrice={basePrice} />
        )}
      </div>
    </DialogContent>
  </Dialog>
);

const PreviewInput = ({ option }) => {
  const choices = option.choices || [];
  switch (option.type) {
    case 'select':
    case 'dropdown':
      return (
        <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white">
          <option value="">{`Select ${option.name || 'option'}...`}</option>
          {choices.map((choice, idx) => (
            <option key={choice.value || `choice-${idx}`} value={choice.value || `choice-${idx}`}>
              {choice.label || 'Unnamed choice'}
              {parseFloat(choice.price) > 0 ? ` (+₹${choice.price})` : ''}
            </option>
          ))}
        </select>
      );
    case 'radio':
      return (
        <div className="space-y-2">
          {choices.map((choice, idx) => (
            <div key={choice.value || `radio-${idx}`} className="flex items-center space-x-2">
              <input type="radio" name={`preview-${option.id}`} id={`${option.id}-${idx}`}
                className="h-4 w-4 text-blue-600 border-gray-300" />
              <label htmlFor={`${option.id}-${idx}`} className="text-sm cursor-pointer">
                {choice.label || 'Unnamed choice'}
                {parseFloat(choice.price) > 0 && <span className="text-green-600 ml-1">(+₹{choice.price})</span>}
              </label>
            </div>
          ))}
        </div>
      );
    case 'checkbox':
      return (
        <div className="space-y-2">
          {choices.map((choice, idx) => (
            <div key={choice.value || `check-${idx}`} className="flex items-center space-x-2">
              <input type="checkbox" id={`${option.id}-check-${idx}`}
                className="h-4 w-4 rounded border-gray-300 text-blue-600" />
              <label htmlFor={`${option.id}-check-${idx}`} className="text-sm cursor-pointer">
                {choice.label || 'Unnamed choice'}
                {parseFloat(choice.price) > 0 && <span className="text-green-600 ml-1">(+₹{choice.price})</span>}
              </label>
            </div>
          ))}
        </div>
      );
    case 'button-select':
      return (
        <div className="flex flex-wrap gap-2">
          {choices.map((choice, idx) => (
            <button key={choice.value || `btn-${idx}`} type="button"
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:border-blue-400 hover:bg-blue-50 transition-colors">
              {choice.label || 'Unnamed'}
              {parseFloat(choice.price) > 0 && <span className="text-green-600 ml-1">+₹{choice.price}</span>}
            </button>
          ))}
        </div>
      );
    case 'textarea':
      return <textarea placeholder={`Enter ${option.name || 'details'}...`} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none" rows={3} />;
    case 'number':
      return <Input type="number" placeholder="0" className="max-w-[120px] border-gray-300" />;
    case 'date':
      return <Input type="date" className="max-w-[180px] border-gray-300" />;
    case 'time':
      return <Input type="time" className="max-w-[150px] border-gray-300" />;
    case 'file_upload':
      return (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-500">Click to upload or drag and drop</p>
        </div>
      );
    default:
      return <Input placeholder={`Enter ${option.name || 'value'}...`} className="max-w-sm border-gray-300" />;
  }
};

const PriceSummary = ({ basePrice }) => (
  <div className="bg-gray-50 p-3 rounded-lg border mt-4">
    <p className="text-xs text-gray-500 mb-2">Price adjusts based on selections:</p>
    <div className="flex justify-between text-sm">
      <span>Base Price:</span>
      <span className="font-medium">₹{basePrice || '---'}</span>
    </div>
    <div className="flex justify-between text-sm text-gray-400">
      <span>+ Selected Options:</span>
      <span>₹0</span>
    </div>
    <div className="flex justify-between text-sm font-semibold border-t pt-2 mt-2">
      <span>Total:</span>
      <span>₹{basePrice || '---'}</span>
    </div>
  </div>
);

export default OptionPreviewDialog;
